import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import { EnvironmentAuthorizationError } from "./auth.ts";
import {
  Base64Pcm16Chunk,
  TrimmedNonEmptyString,
  MessageId,
  ThreadId,
  TurnId,
} from "./baseSchemas.ts";
import { NeucleosDoorSessionError } from "./neucleosDoor.ts";

/**
 * Readback stream contract: Cleo speaks text the client already displays
 * (V-READBACK — text-to-speech output only, no microphone anywhere near it).
 *
 * A readback is transient, display-only derived output. The client sends the
 * text it is showing; synthesized audio streams back for playback and is never
 * persisted — no thread write, no event-log write, no provenance, no retention.
 * Pronunciation shaping (the `cleoVoice` map, e.g. how Cleo says "Neucleos")
 * happens server-side at the synthesis boundary only; displayed text is never
 * rewritten.
 *
 * Wire shape: `readback.speak` is a one-shot streaming RPC. Events arrive as
 * `started` (slot + PCM sample rate), then bounded base64 PCM16 `audio`
 * chunks, `alignment` character timings when the cloud voice provides them,
 * and `done`. Cancelling the stream cancels the synthesis; there is no
 * session id because nothing else references a readback.
 *
 * Slots: omitted means the configured default (local — the keyless path).
 * `cloud` opts into the pinned Cleo persona voice and is refused with
 * `ReadbackNotConfiguredError` when the server has no credential for it; the
 * local path keeps working regardless.
 */
export const READBACK_WS_METHODS = {
  readbackSpeak: "readback.speak",
  replyText: "readback.replyText",
  subscribeReplyText: "readback.subscribeReplyText",
} as const;

/**
 * Which engine slot synthesizes this readback. The default path is local and
 * requires no API key; `cloud` is an opt-in override for the configured
 * persona voice.
 */
export const ReadbackTtsSlot = Schema.Literals(["local", "cloud"]);
export type ReadbackTtsSlot = typeof ReadbackTtsSlot.Type;

/** Bound on one readback request. Clients must report overflow; never silently trim. */
export const READBACK_TEXT_MAX_LENGTH = 32_768;

/** A speech view follows one already committed input; it never submits a turn. */
export const ReadbackReplyTextInput = Schema.Struct({
  threadId: ThreadId,
  userMessageId: MessageId,
  worldKey: TrimmedNonEmptyString,
  sessionId: TrimmedNonEmptyString,
});
export type ReadbackReplyTextInput = typeof ReadbackReplyTextInput.Type;
export const ReadbackReplyTextSnapshot = Schema.Struct({
  ...ReadbackReplyTextInput.fields,
  turnId: Schema.NullOr(TurnId),
  state: Schema.Literals(["waiting", "running", "completed", "interrupted", "error"]),
  messages: Schema.Array(
    Schema.Struct({
      messageId: MessageId,
      text: Schema.String,
      complete: Schema.Boolean,
    }),
  ),
});
export type ReadbackReplyTextSnapshot = typeof ReadbackReplyTextSnapshot.Type;
export class ReadbackReplyTextError extends Schema.TaggedErrorClass<ReadbackReplyTextError>()(
  "ReadbackReplyTextError",
  { detail: Schema.String },
) {}
const ReadbackReplyRpcError = Schema.Union([
  ReadbackReplyTextError,
  EnvironmentAuthorizationError,
  NeucleosDoorSessionError,
]);
export const WsReadbackReplyTextRpc = Rpc.make(READBACK_WS_METHODS.replyText, {
  payload: ReadbackReplyTextInput,
  success: ReadbackReplyTextSnapshot,
  error: ReadbackReplyRpcError,
});
export const WsReadbackSubscribeReplyTextRpc = Rpc.make(READBACK_WS_METHODS.subscribeReplyText, {
  payload: ReadbackReplyTextInput,
  success: ReadbackReplyTextSnapshot,
  error: ReadbackReplyRpcError,
  stream: true,
});

export const ReadbackSpeakInput = Schema.Struct({
  /** Source session for reply speech; its immutable privacy policy still applies. */
  threadId: Schema.optionalKey(ThreadId),
  /** The text to speak, exactly as displayed. */
  text: TrimmedNonEmptyString.check(Schema.isMaxLength(READBACK_TEXT_MAX_LENGTH)),
  /** Slot override for this readback only. Omitted means configured default (local). */
  ttsSlot: Schema.optional(ReadbackTtsSlot),
});
export type ReadbackSpeakInput = typeof ReadbackSpeakInput.Type;

/**
 * One outbound audio chunk at the stream's announced sample rate: the shared
 * bounded wire chunk (`Base64Pcm16Chunk`, 49,152 PCM bytes ≈ 1 s at 24 kHz).
 */
export const ReadbackAudioChunk = Base64Pcm16Chunk;
export type ReadbackAudioChunk = typeof ReadbackAudioChunk.Type;

/**
 * First event on every readback stream: synthesis began, and every following
 * `audio` chunk is PCM16 mono at `sampleRate`.
 */
const ReadbackStartedEvent = Schema.Struct({
  type: Schema.Literal("started"),
  ttsSlot: ReadbackTtsSlot,
  sampleRate: Schema.Number.check(Schema.isBetween({ minimum: 8_000, maximum: 48_000 })),
});

const ReadbackAudioEvent = Schema.Struct({
  type: Schema.Literal("audio"),
  audio: ReadbackAudioChunk,
});

/**
 * Character-level timing for a stretch of synthesized speech, as three
 * parallel arrays. Emitted only when the serving voice reports timestamps
 * (the pinned cloud voice does; the local engine does not). Indices refer to
 * the text as synthesized — after pronunciation shaping — so this drives
 * presence animation, never display-text highlighting.
 */
const ReadbackAlignmentEvent = Schema.Struct({
  type: Schema.Literal("alignment"),
  chars: Schema.Array(Schema.String),
  startMs: Schema.Array(Schema.Number),
  endMs: Schema.Array(Schema.Number),
});

/** Clean end of the readback; the stream completes after this event. */
const ReadbackDoneEvent = Schema.Struct({
  type: Schema.Literal("done"),
});

export const ReadbackStreamEvent = Schema.Union([
  ReadbackStartedEvent,
  ReadbackAudioEvent,
  ReadbackAlignmentEvent,
  ReadbackDoneEvent,
]);
export type ReadbackStreamEvent = typeof ReadbackStreamEvent.Type;

/**
 * The requested slot is not configured on this server — the honest not-run,
 * never a silent fallback. Today this is the cloud slot without its
 * credential gate filled (or with the key refused by the vendor).
 */
export class ReadbackNotConfiguredError extends Schema.TaggedErrorClass<ReadbackNotConfiguredError>()(
  "ReadbackNotConfiguredError",
  {
    detail: Schema.String,
  },
) {
  override get message() {
    return `Readback slot is not configured: ${this.detail}`;
  }
}

/** The synthesis engine failed this readback (worker, model, or provider failure). */
export class ReadbackEngineError extends Schema.TaggedErrorClass<ReadbackEngineError>()(
  "ReadbackEngineError",
  {
    detail: Schema.String,
  },
) {
  override get message() {
    return `Readback synthesis failed: ${this.detail}`;
  }
}

export const ReadbackError = Schema.Union([ReadbackNotConfiguredError, ReadbackEngineError]);
export type ReadbackError = typeof ReadbackError.Type;

const ReadbackRpcError = Schema.Union([ReadbackError, EnvironmentAuthorizationError]);

export const WsReadbackSpeakRpc = Rpc.make(READBACK_WS_METHODS.readbackSpeak, {
  payload: ReadbackSpeakInput,
  success: ReadbackStreamEvent,
  error: ReadbackRpcError,
  stream: true,
});

/**
 * The readback contract as its own group, merged into the served `WsRpcGroup`
 * in rpc.ts. The server side lives in `apps/server/src/speech`: the readback
 * manager applies the `cleoVoice` pronunciation map and synthesizes through
 * the speech worker child process (local slot) or the pinned persona voice
 * (cloud slot). Keeping the group separate lets the acceptance rig serve
 * exactly this surface without the rest of the server graph.
 */
export const ReadbackRpcGroup = RpcGroup.make(
  WsReadbackSpeakRpc,
  WsReadbackReplyTextRpc,
  WsReadbackSubscribeReplyTextRpc,
);
